import { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { Edit2, Trash2, Plus, Pencil } from 'lucide-react';
import { format, parseISO, formatDistanceToNow } from 'date-fns';
import EditDecisionDialog from '@/components/EditDecisionDialog';
import AddFollowUpDialog from '@/components/AddFollowUpDialog';
import EditFollowUpDialog from '@/components/EditFollowUpDialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

export default function DecisionDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { getAuthHeaders, API } = useAuth();
  
  const [decision, setDecision] = useState(null);
  const [allDecisions, setAllDecisions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showAddFollowUp, setShowAddFollowUp] = useState(false);
  const [editingFollowUp, setEditingFollowUp] = useState(null);
  const [updating, setUpdating] = useState(false);
  const [settingOutcome, setSettingOutcome] = useState(false);
  const [reflectionText, setReflectionText] = useState('');
  const [savingReflection, setSavingReflection] = useState(false);
  const [showReflectionInput, setShowReflectionInput] = useState(false);
  
  // New state for additional features
  const [statusNote, setStatusNote] = useState('');
  const [showStatusNote, setShowStatusNote] = useState(false);
  const [savingStatus, setSavingStatus] = useState(false);
  const [showRelatedPicker, setShowRelatedPicker] = useState(false);
  const [savingRelated, setSavingRelated] = useState(false);
  const [savingImportant, setSavingImportant] = useState(false);
  
  // Discovery hints state
  const [hints, setHints] = useState({ important: false, related: false, aging: false });

  const fetchHints = async () => {
    try {
      const response = await axios.get(`${API}/hints`, {
        headers: getAuthHeaders()
      });
      setHints(response.data);
    } catch (error) {
      // Silently fail - hints are non-critical
      console.error('Error fetching hints:', error);
    }
  };

  const dismissHint = async (hintName) => {
    try {
      await axios.post(`${API}/hints/dismiss/${hintName}`, {}, {
        headers: getAuthHeaders()
      });
      setHints(prev => ({ ...prev, [hintName]: false }));
    } catch (error) {
      console.error('Error dismissing hint:', error);
    }
  };

  const fetchDecision = async () => {
    try {
      const response = await axios.get(`${API}/decisions/${id}`, {
        headers: getAuthHeaders()
      });
      setDecision(response.data);
      setReflectionText(response.data.reflection || '');
      setStatusNote(response.data.status_note || '');
    } catch (error) {
      console.error('Error fetching decision:', error);
      toast.error('Decision not found');
      navigate(-1);
    } finally {
      setLoading(false);
    }
  };

  const fetchAllDecisions = async () => {
    try {
      const response = await axios.get(`${API}/decisions`, {
        headers: getAuthHeaders()
      });
      setAllDecisions(response.data);
    } catch (error) {
      console.error('Error fetching decisions:', error);
    }
  };

  useEffect(() => {
    fetchDecision();
    fetchAllDecisions();
    fetchHints();
  }, [id]);

  const openFollowUps = decision?.follow_ups?.filter(f => f.status === 'open') || [];
  const doneFollowUps = decision?.follow_ups?.filter(f => f.status === 'done') || [];
  const allFollowUpsDone = decision?.follow_ups?.length > 0 && openFollowUps.length === 0;
  const canSetOutcome = allFollowUpsDone || decision?.follow_ups?.length === 0;

  const formatDate = (dateStr) => {
    try {
      return format(parseISO(dateStr), 'MMMM d, yyyy');
    } catch {
      return dateStr;
    }
  };

  const getDecisionAge = (dateStr) => {
    try {
      const date = parseISO(dateStr);
      return formatDistanceToNow(date, { addSuffix: false });
    } catch {
      return null;
    }
  };

  const formatOutcome = (outcome) => {
    const labels = {
      'as_expected': 'As expected',
      'partly_as_expected': 'Partly as expected',
      'not_as_expected': 'Not as expected',
      // Legacy values for backwards compatibility
      'good': 'As expected',
      'mixed': 'Partly as expected',
      'poor': 'Not as expected'
    };
    return labels[outcome] || outcome;
  };

  const handleToggleFollowUp = async (followUpId, currentStatus) => {
    setUpdating(true);
    const newStatus = currentStatus === 'open' ? 'done' : 'open';
    
    try {
      const response = await axios.put(
        `${API}/decisions/${decision.id}/followups/${followUpId}`,
        { status: newStatus },
        { headers: getAuthHeaders() }
      );
      setDecision(response.data);
    } catch (error) {
      toast.error('Failed to update follow-up');
    } finally {
      setUpdating(false);
    }
  };

  const handleDeleteFollowUp = async (followUpId) => {
    try {
      const response = await axios.delete(
        `${API}/decisions/${decision.id}/followups/${followUpId}`,
        { headers: getAuthHeaders() }
      );
      setDecision(response.data);
      toast.success('Follow-up removed');
    } catch (error) {
      toast.error('Failed to delete follow-up');
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/decisions/${decision.id}`, {
        headers: getAuthHeaders()
      });
      toast.success('Decision deleted');
      navigate(-1);
    } catch (error) {
      toast.error('Failed to delete decision');
    }
  };

  const handleSetOutcome = async (outcome) => {
    if (settingOutcome) return;
    const newOutcome = decision.outcome === outcome ? null : outcome;
    
    setSettingOutcome(true);
    try {
      const response = await axios.put(
        `${API}/decisions/${decision.id}`,
        { outcome: newOutcome },
        { headers: getAuthHeaders() }
      );
      setDecision(response.data);
      if (newOutcome) toast.success('Outcome recorded');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to set outcome');
    } finally {
      setSettingOutcome(false);
    }
  };

  const handleSaveReflection = async () => {
    if (savingReflection) return;
    
    setSavingReflection(true);
    try {
      const response = await axios.put(
        `${API}/decisions/${decision.id}`,
        { reflection: reflectionText.trim() },
        { headers: getAuthHeaders() }
      );
      setDecision(response.data);
      setShowReflectionInput(false);
      toast.success('Reflection saved');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save reflection');
    } finally {
      setSavingReflection(false);
    }
  };

  // New handlers for additional features
  const handleStatusChange = async (newStatus) => {
    if (savingStatus) return;
    
    setSavingStatus(true);
    try {
      const response = await axios.put(
        `${API}/decisions/${decision.id}`,
        { status: newStatus },
        { headers: getAuthHeaders() }
      );
      setDecision(response.data);
      
      // Show status note input for revisited/superseded
      if (newStatus !== 'active') {
        setShowStatusNote(true);
      } else {
        setShowStatusNote(false);
        setStatusNote('');
      }
    } catch (error) {
      toast.error('Failed to update status');
    } finally {
      setSavingStatus(false);
    }
  };

  const handleSaveStatusNote = async () => {
    if (savingStatus) return;
    
    setSavingStatus(true);
    try {
      const response = await axios.put(
        `${API}/decisions/${decision.id}`,
        { status_note: statusNote.trim() },
        { headers: getAuthHeaders() }
      );
      setDecision(response.data);
      setShowStatusNote(false);
      toast.success('Note saved');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save note');
    } finally {
      setSavingStatus(false);
    }
  };

  const handleToggleImportant = async () => {
    if (savingImportant) return;
    
    setSavingImportant(true);
    try {
      const response = await axios.put(
        `${API}/decisions/${decision.id}`,
        { important: !decision.important },
        { headers: getAuthHeaders() }
      );
      setDecision(response.data);
      // Dismiss hint when user marks something as important
      if (!decision.important && hints.important) {
        dismissHint('important');
      }
    } catch (error) {
      toast.error('Failed to update');
    } finally {
      setSavingImportant(false);
    }
  };

  const handleAddRelated = async (relatedId) => {
    if (savingRelated) return;
    
    const currentRelated = decision.related_decisions || [];
    if (currentRelated.includes(relatedId)) return;
    
    setSavingRelated(true);
    try {
      const response = await axios.put(
        `${API}/decisions/${decision.id}`,
        { related_decisions: [...currentRelated, relatedId] },
        { headers: getAuthHeaders() }
      );
      setDecision(response.data);
      setShowRelatedPicker(false);
      // Dismiss hint when user links decisions
      if (hints.related) {
        dismissHint('related');
      }
    } catch (error) {
      toast.error('Failed to add related decision');
    } finally {
      setSavingRelated(false);
    }
  };

  const handleRemoveRelated = async (relatedId) => {
    if (savingRelated) return;
    
    const currentRelated = decision.related_decisions || [];
    
    setSavingRelated(true);
    try {
      const response = await axios.put(
        `${API}/decisions/${decision.id}`,
        { related_decisions: currentRelated.filter(id => id !== relatedId) },
        { headers: getAuthHeaders() }
      );
      setDecision(response.data);
    } catch (error) {
      toast.error('Failed to remove related decision');
    } finally {
      setSavingRelated(false);
    }
  };

  // Get titles for related decisions
  const getRelatedDecisionTitle = (relatedId) => {
    const related = allDecisions.find(d => d.id === relatedId);
    return related?.title || 'Unknown decision';
  };

  // Filter out current decision and already related ones for picker
  const availableDecisions = allDecisions.filter(
    d => d.id !== decision?.id && !(decision?.related_decisions || []).includes(d.id)
  );

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-muted-foreground">Loading...</div>
      </div>
    );
  }

  if (!decision) return null;

  const decisionAge = getDecisionAge(decision.created_at || decision.date);

  return (
    <div className="min-h-screen bg-background" data-testid="decision-detail-page">
      {/* Header */}
      <header className="sticky top-0 z-10 bg-background/95 backdrop-blur border-b border-border">
        <div className="max-w-2xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="text-2xl font-medium tracking-tight hover:text-accent transition-colors">
            Decided
          </Link>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setShowEditDialog(true)}
              data-testid="edit-decision-button"
            >
              <Edit2 size={18} />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setShowDeleteDialog(true)}
              data-testid="delete-decision-button"
            >
              <Trash2 size={18} />
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-2xl mx-auto px-6 py-12">
        {/* Decision Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-3 text-sm text-muted-foreground flex-wrap">
            <span>{formatDate(decision.date)}</span>
            {decision.decision_type && (
              <span className="capitalize">{decision.decision_type}</span>
            )}
            {decision.outcome && (
              <span>{formatOutcome(decision.outcome)}</span>
            )}
          </div>
          <h1 className="text-3xl font-medium tracking-tight font-serif text-foreground mb-4">
            {decision.title}
          </h1>
          <p className="text-muted-foreground leading-relaxed">
            {decision.context}
          </p>
          
          {/* Decision Aging */}
          {decisionAge && (
            <div className="mt-4">
              <p className="text-sm text-muted-foreground/60">
                Decision made {decisionAge} ago
              </p>
              {/* Discovery hint for aging (optional) */}
              {hints.aging && (
                <p 
                  className="text-xs text-muted-foreground/40 mt-1 cursor-pointer"
                  onClick={() => dismissHint('aging')}
                >
                  Time adds context to decisions.
                </p>
              )}
            </div>
          )}
          
          {/* Important Flag */}
          {decision.important && (
            <p className="text-sm text-muted-foreground mt-2">
              Marked as important
            </p>
          )}
          
          {/* Status (if not active) */}
          {decision.status && decision.status !== 'active' && (
            <div className="mt-2">
              <p className="text-sm text-muted-foreground capitalize">
                {decision.status}
              </p>
              {decision.status_note && (
                <p className="text-sm text-muted-foreground/70 mt-1">
                  {decision.status_note}
                </p>
              )}
            </div>
          )}
        </div>

        {/* Status & Important Section */}
        <section className="border-t border-border pt-8 mb-8">
          <h2 className="text-sm tracking-wide text-muted-foreground uppercase mb-6">
            Status
          </h2>
          
          <div className="space-y-4">
            {/* Status selector */}
            <div>
              <Select
                value={decision.status || 'active'}
                onValueChange={handleStatusChange}
                disabled={savingStatus}
              >
                <SelectTrigger className="w-full max-w-xs" data-testid="status-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="revisited">Revisited</SelectItem>
                  <SelectItem value="superseded">Superseded</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {/* Status note (for revisited/superseded) */}
            {(decision.status === 'revisited' || decision.status === 'superseded') && (
              <div>
                {decision.status_note && !showStatusNote ? (
                  <div>
                    <p className="text-sm text-muted-foreground/70">
                      {decision.status_note}
                    </p>
                    <button
                      onClick={() => {
                        setStatusNote(decision.status_note);
                        setShowStatusNote(true);
                      }}
                      className="text-xs text-muted-foreground/50 hover:text-muted-foreground mt-2 transition-colors"
                    >
                      Edit note
                    </button>
                  </div>
                ) : showStatusNote ? (
                  <div>
                    <Input
                      value={statusNote}
                      onChange={(e) => setStatusNote(e.target.value.slice(0, 200))}
                      placeholder="Why was this decision revisited or superseded?"
                      className="text-sm"
                      data-testid="status-note-input"
                    />
                    <div className="flex items-center justify-between mt-2">
                      <span className="text-xs text-muted-foreground/50">
                        {statusNote.length}/200
                      </span>
                      <div className="flex gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setShowStatusNote(false);
                            setStatusNote(decision.status_note || '');
                          }}
                        >
                          Cancel
                        </Button>
                        <Button
                          size="sm"
                          onClick={handleSaveStatusNote}
                          disabled={savingStatus}
                        >
                          Save
                        </Button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <button
                    onClick={() => setShowStatusNote(true)}
                    className="text-sm text-muted-foreground/50 hover:text-muted-foreground transition-colors"
                  >
                    Add a note about why
                  </button>
                )}
              </div>
            )}
            
            {/* Important toggle */}
            <div className="pt-4 border-t border-border/50">
              <button
                onClick={handleToggleImportant}
                disabled={savingImportant}
                className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                data-testid="important-toggle"
              >
                {decision.important ? 'Remove important mark' : 'Mark as important'}
              </button>
              {/* Discovery hint for important */}
              {hints.important && !decision.important && (
                <p className="text-xs text-muted-foreground/40 mt-2">
                  You can mark a decision as important.
                </p>
              )}
            </div>
          </div>
        </section>

        {/* Related Decisions Section */}
        <section className="border-t border-border pt-8 mb-8">
          <h2 className="text-sm tracking-wide text-muted-foreground uppercase mb-6">
            Related Decisions
          </h2>
          
          {(decision.related_decisions || []).length > 0 ? (
            <div className="space-y-2 mb-4">
              {decision.related_decisions.map((relatedId) => (
                <div 
                  key={relatedId}
                  className="flex items-center justify-between group"
                >
                  <Link 
                    to={`/decision/${relatedId}`}
                    className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                  >
                    {getRelatedDecisionTitle(relatedId)}
                  </Link>
                  <button
                    onClick={() => handleRemoveRelated(relatedId)}
                    className="text-xs text-muted-foreground/50 hover:text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity"
                    disabled={savingRelated}
                  >
                    Remove
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <div className="mb-4">
              <p className="text-sm text-muted-foreground/50">
                No related decisions.
              </p>
              {/* Discovery hint for related decisions */}
              {hints.related && (
                <p className="text-xs text-muted-foreground/40 mt-2">
                  Decisions can be linked when relevant.
                </p>
              )}
            </div>
          )}
          
          {showRelatedPicker ? (
            <div>
              {availableDecisions.length > 0 ? (
                <Select
                  onValueChange={(value) => handleAddRelated(value)}
                  disabled={savingRelated}
                >
                  <SelectTrigger className="w-full" data-testid="related-picker">
                    <SelectValue placeholder="Select a decision to link..." />
                  </SelectTrigger>
                  <SelectContent>
                    {availableDecisions.map((d) => (
                      <SelectItem key={d.id} value={d.id}>
                        {d.title}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <p className="text-sm text-muted-foreground/50">
                  No other decisions available to link.
                </p>
              )}
              <button
                onClick={() => setShowRelatedPicker(false)}
                className="text-xs text-muted-foreground/50 hover:text-muted-foreground mt-2 transition-colors"
              >
                Cancel
              </button>
            </div>
          ) : (
            <button
              onClick={() => setShowRelatedPicker(true)}
              className="text-sm text-muted-foreground/50 hover:text-muted-foreground transition-colors"
              data-testid="add-related-button"
            >
              Link a related decision
            </button>
          )}
        </section>

        {/* Follow-ups Section */}
        <section className="border-t border-border pt-8 mb-8">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-sm tracking-wide text-muted-foreground uppercase">
              Follow-up Actions
            </h2>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowAddFollowUp(true)}
              className="h-8 text-xs"
              data-testid="add-followup-button"
            >
              <Plus size={14} className="mr-1" />
              Add
            </Button>
          </div>

          {decision.follow_ups?.length === 0 ? (
            <p className="text-sm text-muted-foreground/70">
              No follow-up actions yet.
            </p>
          ) : (
            <div className="space-y-3">
              {openFollowUps.map((followUp) => (
                <div 
                  key={followUp.id}
                  className="flex items-start gap-3 p-3 rounded-lg hover:bg-secondary/50 transition-colors group"
                >
                  <Checkbox
                    checked={false}
                    onCheckedChange={() => handleToggleFollowUp(followUp.id, 'open')}
                    disabled={updating}
                    className="mt-0.5"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-foreground">{followUp.description}</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      Due {format(parseISO(followUp.due_date), 'MMM d')}
                    </p>
                  </div>
                  <div className="flex items-center gap-1 md:opacity-0 md:group-hover:opacity-100 transition-opacity">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => setEditingFollowUp(followUp)}
                      className="h-7 w-7 text-muted-foreground hover:text-foreground"
                    >
                      <Pencil size={14} />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDeleteFollowUp(followUp.id)}
                      className="h-7 w-7 text-muted-foreground hover:text-destructive"
                    >
                      <Trash2 size={14} />
                    </Button>
                  </div>
                </div>
              ))}

              {doneFollowUps.length > 0 && (
                <div className="pt-3 mt-3 border-t border-border/50">
                  <p className="text-xs text-muted-foreground/70 mb-2">Completed</p>
                  {doneFollowUps.map((followUp) => (
                    <div 
                      key={followUp.id}
                      className="flex items-start gap-3 p-3 rounded-lg hover:bg-secondary/50 transition-colors group"
                    >
                      <Checkbox
                        checked={true}
                        onCheckedChange={() => handleToggleFollowUp(followUp.id, 'done')}
                        disabled={updating}
                        className="mt-0.5"
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-muted-foreground line-through">{followUp.description}</p>
                        <p className="text-xs text-muted-foreground/70 mt-1">
                          Due {format(parseISO(followUp.due_date), 'MMM d')}
                        </p>
                      </div>
                      <div className="flex items-center gap-1 md:opacity-0 md:group-hover:opacity-100 transition-opacity">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => setEditingFollowUp(followUp)}
                          className="h-7 w-7 text-muted-foreground hover:text-foreground"
                        >
                          <Pencil size={14} />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDeleteFollowUp(followUp.id)}
                          className="h-7 w-7 text-muted-foreground hover:text-destructive"
                        >
                          <Trash2 size={14} />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </section>

        {/* Outcome Section */}
        {canSetOutcome && (
          <section className="border-t border-border pt-8 mb-8">
            <h2 className="text-sm tracking-wide text-muted-foreground uppercase mb-3">
              Decision Outcome
            </h2>
            <p className="text-sm text-muted-foreground/70 mb-4">
              How did this decision compare to your expectations?
            </p>
            <div className="flex flex-wrap gap-2">
              {[
                { value: 'as_expected', label: 'As expected' },
                { value: 'partly_as_expected', label: 'Partly as expected' },
                { value: 'not_as_expected', label: 'Not as expected' }
              ].map((option) => (
                <button
                  key={option.value}
                  onClick={() => handleSetOutcome(option.value)}
                  disabled={settingOutcome}
                  className={`px-4 py-2 text-sm rounded-sm border transition-colors ${
                    decision.outcome === option.value
                      ? 'border-foreground bg-foreground text-background'
                      : 'border-border text-muted-foreground hover:border-foreground hover:text-foreground'
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
            <p className="text-xs text-muted-foreground/50 mt-3">
              You can leave this unset.
            </p>
            {decision.outcome && (
              <p className="text-xs text-muted-foreground/50 mt-1">
                Click again to clear.
              </p>
            )}
          </section>
        )}

        {/* Reflection Section */}
        {decision.outcome && (
          <section className="border-t border-border pt-8">
            <h2 className="text-sm tracking-wide text-muted-foreground uppercase mb-3">
              Reflection
            </h2>
            
            {decision.reflection && !showReflectionInput ? (
              <div>
                <p className="text-muted-foreground leading-relaxed">
                  {decision.reflection}
                </p>
                <button
                  onClick={() => {
                    setReflectionText(decision.reflection);
                    setShowReflectionInput(true);
                  }}
                  className="text-xs text-muted-foreground/50 hover:text-muted-foreground mt-4 transition-colors"
                >
                  Edit reflection
                </button>
              </div>
            ) : showReflectionInput ? (
              <div>
                <Textarea
                  value={reflectionText}
                  onChange={(e) => setReflectionText(e.target.value.slice(0, 500))}
                  placeholder="Anything worth remembering about how this decision played out?"
                  className="min-h-[100px] resize-none"
                />
                <div className="flex items-center justify-between mt-3">
                  <span className="text-xs text-muted-foreground/50">
                    {reflectionText.length}/500
                  </span>
                  <div className="flex gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setShowReflectionInput(false);
                        setReflectionText(decision.reflection || '');
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      size="sm"
                      onClick={handleSaveReflection}
                      disabled={savingReflection || !reflectionText.trim()}
                    >
                      {savingReflection ? 'Saving...' : 'Save'}
                    </Button>
                  </div>
                </div>
              </div>
            ) : (
              <button
                onClick={() => setShowReflectionInput(true)}
                className="text-sm text-muted-foreground/70 hover:text-muted-foreground transition-colors"
              >
                Anything worth remembering about how this decision played out?
              </button>
            )}
          </section>
        )}

        {/* Message when outcome cannot be set */}
        {!canSetOutcome && openFollowUps.length > 0 && (
          <section className="border-t border-border pt-8">
            <p className="text-sm text-muted-foreground/50">
              Complete all follow-ups to record the outcome.
            </p>
          </section>
        )}
      </main>

      {/* Dialogs */}
      <AddFollowUpDialog
        open={showAddFollowUp}
        onOpenChange={setShowAddFollowUp}
        decisionId={decision.id}
        onAdded={setDecision}
      />

      <EditDecisionDialog
        open={showEditDialog}
        onOpenChange={setShowEditDialog}
        decision={decision}
        onUpdated={setDecision}
      />

      {editingFollowUp && (
        <EditFollowUpDialog
          open={!!editingFollowUp}
          onOpenChange={(open) => !open && setEditingFollowUp(null)}
          followUp={editingFollowUp}
          decisionId={decision.id}
          onUpdated={setDecision}
        />
      )}

      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this decision?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete the decision and all its follow-up actions.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
